import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { ariaCardSurfaceSx } from '../ariaHandsoffSurfaces';
import { ARIA_CARD_ACCENT } from '../ariaMessageTypes';

const MetaRow = ({ icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box sx={{ color: 'text.disabled', display: 'flex', flexShrink: 0 }}>{icon}</Box>
    <Typography
      noWrap
      sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500, lineHeight: 1.35 }}
    >
      {children}
    </Typography>
  </Box>
);

/**
 * Inline event recommendation card (message.data.ariaMessageType = event_recommendation).
 * Admin Handsoff: display-only — buttons reflect status but never trigger actions.
 */
const AriaEventRecommendationCard = ({ event }) => {
  if (!event?.eventId) return null;

  const showActions = Boolean(event.upcomingEvent) || event.isBooked;

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 340 },
        maxWidth: '100%',
        minWidth: { sm: 300 },
        borderRadius: '16px',
        overflow: 'hidden',
        ...ariaCardSurfaceSx,
        boxShadow: 2,
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', pt: '56%', bgcolor: 'action.hover' }}>
        {event.coverImageUrl ? (
          <Box
            component="img"
            src={event.coverImageUrl}
            alt=""
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            px: 1,
            py: 0.375,
            borderRadius: '999px',
            bgcolor: 'rgba(17, 24, 39, 0.62)',
            color: '#ffffff',
            fontSize: '0.625rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            lineHeight: 1.3,
          }}
        >
          ✦ Aria pick for you
        </Box>
        {event.seatsRemaining != null ? (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              px: 1,
              py: 0.375,
              borderRadius: '999px',
              bgcolor: 'rgba(255, 255, 255, 0.92)',
              color: 'text.secondary',
              fontSize: '0.625rem',
              fontWeight: 700,
              lineHeight: 1.3,
            }}
          >
            {event.seatsRemaining} seat{event.seatsRemaining === 1 ? '' : 's'} left
          </Box>
        ) : null}
      </Box>

      <Box sx={{ px: 1.5, pt: 1.25, pb: 1.5 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.9375rem',
            color: 'text.primary',
            lineHeight: 1.3,
            mb: 0.75,
          }}
        >
          {event.title}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            mb: event.whyThisOne ? 1 : 1.25,
          }}
        >
          {event.dateLine ? (
            <MetaRow icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />}>
              {event.dateLine}
            </MetaRow>
          ) : null}
          {event.locationLine ? (
            <MetaRow icon={<PlaceOutlinedIcon sx={{ fontSize: 14 }} />}>
              {event.locationLine}
            </MetaRow>
          ) : null}
        </Box>

        {event.whyThisOne ? (
          <Box
            sx={{
              bgcolor: (theme) => theme.portal?.accentSoft ?? theme.palette.action.hover,
              border: '1px solid',
              borderColor: (theme) => theme.portal?.accentMuted ?? theme.palette.divider,
              borderRadius: '12px',
              px: 1.25,
              py: 1,
              mb: 1.25,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.625rem',
                fontWeight: 800,
                letterSpacing: '0.06em',
                color: 'primary.main',
                mb: 0.35,
              }}
            >
              WHY THIS ONE
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.45 }}>
              {event.whyThisOne}
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          {event.priceLine ? (
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.primary' }}>
              {event.priceLine}
            </Typography>
          ) : (
            <Box />
          )}

          {showActions ? (
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
              {event.upcomingEvent ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    borderRadius: '999px',
                    px: 1.5,
                    minWidth: 0,
                    color: 'text.secondary',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    '&.Mui-disabled': {
                      color: 'text.disabled',
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  View
                </Button>
              ) : null}
              <Button
                size="small"
                variant="contained"
                disabled
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  borderRadius: '999px',
                  px: 1.5,
                  minWidth: 0,
                  bgcolor: ARIA_CARD_ACCENT,
                  boxShadow: 'none',
                  '&.Mui-disabled': {
                    bgcolor: event.isBooked ? 'action.disabledBackground' : ARIA_CARD_ACCENT,
                    color: event.isBooked ? 'text.secondary' : 'common.white',
                    opacity: event.isBooked ? 1 : 0.55,
                  },
                }}
              >
                {event.isBooked ? 'Booked' : 'Book'}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default AriaEventRecommendationCard;
